import React, { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Coins, Gift, History, PiggyBank } from 'lucide-react';
import { useLab, studentBase, resolveLabChildId } from '../LabContext';
import { LabLayout, Card, NimnyArt } from '../LabUI';
import { formatNims } from '../utils/nims';

// "Копилка Нимни" — child-facing prize-token inventory.
// No real-world currency, no exchange rate, no payout language.
const KopilkaPage = () => {
  const { t, user, getNimBalance, getNimHistory, refreshBackendStudent } = useLab();
  const params = useParams();
  const location = useLocation();
  const studentId = resolveLabChildId({
    routeStudentId: params.studentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(studentId);
  const balance = getNimBalance(studentId);
  const history = getNimHistory(studentId);

  // Parent preview: pull the freshest balance + history from the backend so
  // the parent sees what the child actually earned, even though they never
  // logged in as the child.
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  useEffect(() => {
    if (!isParent || !studentId) return;
    refreshBackendStudent(studentId).catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParent, studentId]);

  return (
    <LabLayout>
      <Link
        to={base}
        className="inline-flex items-center gap-1.5 text-amber-200/75 hover:text-amber-200 text-sm font-sans"
        data-testid="kopilka-back"
      >
        <ChevronLeft className="w-4 h-4" />
        {t('back')}
      </Link>

      {/* Header */}
      <section className="mt-5 text-center">
        <div className="inline-flex w-16 h-16 rounded-full bg-amber-300/10 border border-amber-300/25 items-center justify-center text-amber-200/90">
          <PiggyBank className="w-8 h-8" />
        </div>
        <h1
          className="mt-4 text-3xl text-[#f3d9a4] leading-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="kopilka-title"
        >
          {t('kopilkaTitle')}
        </h1>
        <p className="mt-3 text-amber-100/80 font-serif text-sm leading-relaxed px-2">
          {t('kopilkaIntro')}
        </p>
      </section>

      {/* Balance card */}
      <Card className="mt-6 p-5 text-center" data-testid="kopilka-balance-card">
        <div className="text-amber-200/65 font-sans text-xs uppercase tracking-wider">
          {t('kopilkaBalanceLabel')}
        </div>
        <div
          className="mt-2 text-4xl text-[#f3d9a4]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="kopilka-balance-value"
        >
          {formatNims(balance)}
        </div>
      </Card>

      {/* Prize Shop link */}
      <Link
        to={`${base}/prize-shop`}
        className="mt-3 block"
        data-testid="kopilka-prize-shop-link"
      >
        <Card className="p-4 flex items-center gap-3 hover:bg-amber-300/5 transition-colors">
          <span className="w-10 h-10 rounded-full bg-amber-300/12 border border-amber-300/25 flex items-center justify-center text-amber-200/95">
            <Gift className="w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[#f5ebe0]/95 font-sans">Лавка Нимни / Nimny Prize Shop</div>
            <div className="text-amber-200/65 text-xs font-serif">
              Посмотри, на какие призы хватает нимов.
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-200/45" />
        </Card>
      </Link>

      {/* History */}
      <Card className="mt-5 p-5" data-testid="kopilka-history-card">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-amber-200/70" />
          <h2
            className="text-base text-[#f3d9a4]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('kopilkaHistoryTitle')}
          </h2>
        </div>
        {history.length === 0 ? (
          <p
            className="text-amber-200/70 font-serif text-sm"
            data-testid="kopilka-empty"
          >
            {t('kopilkaEmpty')}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="kopilka-history-list">
            {history.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-xl border border-amber-300/15 bg-[#0e1530]/60"
                data-testid="kopilka-history-item"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <Coins className="w-4 h-4 text-amber-300/80 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[#f5ebe0]/90 font-sans text-sm truncate">
                      {entry.lessonTitle || entry.lessonId}
                    </div>
                    <div className="text-amber-200/65 font-serif text-xs">
                      {entry.label}
                    </div>
                  </div>
                </div>
                <div className="text-emerald-200/90 font-sans text-sm shrink-0">
                  +{formatNims(entry.amount)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="mt-8 text-center text-xs text-amber-200/40 font-sans">
        <span className="lab-brand-mark">Nimny</span>{' '}
        {t('kopilkaCardSubtitle')}
      </div>
    </LabLayout>
  );
};

export default KopilkaPage;
